/**
 * Submits outbound replies to the OncoEMR task reply outbox endpoint.
 * @module useOncoemrTaskReply
 */

import { useCallback, useState } from "react";

import { replyOncoemrTask } from "../../../../../api/postCall/replyOncoemrTask";

/** Inputs for {@link useOncoemrTaskReply}. */
export interface UseOncoemrTaskReplyParams {
  orgId: string;
  taskRowId?: string;
  userId?: string;
  /** Called after the API accepts the queued reply (202). */
  onReplyQueued?: () => void | Promise<void>;
}

/**
 * Wraps `POST .../reply` with submit state and client-generated idempotency keys.
 *
 * @param params - Org scope, selected task row, user id, and post-success callback.
 * @returns `submitReply` and `isSubmitting` for the conversation composer.
 */
export const useOncoemrTaskReply = ({
  orgId,
  taskRowId,
  userId,
  onReplyQueued,
}: UseOncoemrTaskReplyParams) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitReply = useCallback(
    async (body: string) => {
      const trimmedBody = body.trim();
      if (!trimmedBody) {
        return;
      }

      if (!orgId || !taskRowId || !userId) {
        throw new Error("Missing task or organization context.");
      }

      setIsSubmitting(true);

      try {
        const result = await replyOncoemrTask(taskRowId, {
          org_id: orgId,
          user_id: userId,
          idempotency_key: crypto.randomUUID(),
          body: trimmedBody,
        });
        await onReplyQueued?.();
        return result;
      } finally {
        setIsSubmitting(false);
      }
    },
    [orgId, taskRowId, userId, onReplyQueued],
  );

  return { submitReply, isSubmitting };
};
